import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './WeeklyScorecard.css';

interface Props {
  storeId: string;
}

interface ScorecardMetrics {
  total: number;
  responseRate: number;
  recoveryRate: number;
  avgResponseTime: string;
  overdueCount: number;
}

interface Scorecard {
  weekStart: string;
  current: ScorecardMetrics;
  previous: ScorecardMetrics;
}

export const WeeklyScorecard: React.FC<Props> = ({ storeId }) => {
  const [scorecard, setScorecard] = useState<Scorecard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScorecard();
  }, [storeId]);

  const fetchScorecard = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.recoveryScorecard(storeId));
      if (response.ok) {
        const data = await response.json();
        setScorecard(data.scorecard);
      }
    } catch (err) {
      console.error('Failed to fetch scorecard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="scorecard-loading">Loading scorecard...</div>;
  if (!scorecard) return <div className="scorecard-empty">No scorecard data available</div>;

  const getTrend = (current: number, previous: number): { arrow: string; color: string; pct: string } => {
    if (previous === 0) return { arrow: '-', color: '#6b7280', pct: '' };
    const pct = ((current - previous) / previous * 100).toFixed(0);
    const isPositive = Number(pct) > 0;
    return {
      arrow: isPositive ? 'fa-arrow-up' : Number(pct) < 0 ? 'fa-arrow-down' : 'fa-minus',
      color: isPositive ? '#22c55e' : Number(pct) < 0 ? '#ef4444' : '#6b7280',
      pct: isPositive ? `+${pct}%` : `${pct}%`
    };
  };

  const getOverdueTrend = (current: number, previous: number) => {
    const t = getTrend(current, previous);
    // For overdue, lower is better - invert colors
    return {
      ...t,
      color: current <= previous ? '#22c55e' : '#ef4444'
    };
  };

  const getGrade = (responseRate: number, recoveryRate: number): { grade: string; color: string } => {
    const avg = (responseRate + recoveryRate) / 2;
    if (avg >= 90) return { grade: 'A', color: '#22c55e' };
    if (avg >= 75) return { grade: 'B', color: '#84cc16' };
    if (avg >= 60) return { grade: 'C', color: '#f59e0b' };
    if (avg >= 40) return { grade: 'D', color: '#f97316' };
    return { grade: 'F', color: '#ef4444' };
  };

  const grade = getGrade(scorecard.current.responseRate, scorecard.current.recoveryRate);

  const metrics = [
    { label: 'Total Workflows', current: scorecard.current.total, previous: scorecard.previous.total, format: (v: number) => v.toString() },
    { label: 'Response Rate', current: scorecard.current.responseRate, previous: scorecard.previous.responseRate, format: (v: number) => `${v}%` },
    { label: 'Recovery Rate', current: scorecard.current.recoveryRate, previous: scorecard.previous.recoveryRate, format: (v: number) => `${v}%` },
    { label: 'Avg Response Time', current: scorecard.current.avgResponseTime, previous: scorecard.previous.avgResponseTime, isText: true },
  ];

  return (
    <div className="weekly-scorecard">
      <div className="scorecard-header">
        <div>
          <h3>Weekly Recovery Scorecard</h3>
          <p>Week of {scorecard.weekStart}</p>
        </div>
        <div className="scorecard-grade" style={{ backgroundColor: grade.color }}>
          {grade.grade}
        </div>
      </div>

      <table className="scorecard-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>This Week</th>
            <th>Last Week</th>
            <th>Trend</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map(m => {
            const trend = m.isText ? { arrow: 'fa-minus', color: '#6b7280', pct: '' } : getTrend(m.current as number, m.previous as number);
            return (
              <tr key={m.label}>
                <td className="metric-label">{m.label}</td>
                <td className="metric-value">{m.isText ? (m.current as string) : (m.format as (v: number) => string)(m.current as number)}</td>
                <td className="metric-prev">{m.isText ? (m.previous as string) : (m.format as (v: number) => string)(m.previous as number)}</td>
                <td><span className="trend" style={{ color: trend.color }}><i className={`fas ${trend.arrow}`}></i> {trend.pct}</span></td>
              </tr>
            );
          })}
          <tr>
            <td className="metric-label">Overdue Count</td>
            <td className="metric-value" style={{ color: scorecard.current.overdueCount > 0 ? '#ef4444' : '#22c55e' }}>{scorecard.current.overdueCount}</td>
            <td className="metric-prev">{scorecard.previous.overdueCount}</td>
            <td>
              {(() => {
                const t = getOverdueTrend(scorecard.current.overdueCount, scorecard.previous.overdueCount);
                return <span className="trend" style={{ color: t.color }}><i className={`fas ${t.arrow}`}></i> {t.pct}</span>;
              })()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
